<template>
  <toolbar-container>
    <paywall-upgrade-dialog
      v-if="upgradeDialog"
      @close="upgradeDialog=false"
    />
    <feedback-details-dialog
      v-if="detailsDialog && selectedFeedback"
      :feedback="selectedFeedback"
      @close="detailsDialog=false"
    />
    <contact-message-dialog
      v-if="messageDialog && selectedFeedback?.reservation"
      visible
      :reservation="selectedFeedback.reservation"
      :feedback-id="selectedFeedback.id"
      @close="closeMessageDialog"
    />
    <template #toolbar>
      <main-toolbar>
        <date-range-picker-field
          :disabled="filterDateRangeDisabled"
          :value="filterDateRange"
          content-style="min-width: 200px; max-width: 200px"
          content-class="mt-2 pr-4"
          :label="$t('label.date_range')"
          disable-future
          @input="filterDateRangeSelected"
        />
        <template v-if="$vuetify.breakpoint.mdAndUp">
          <v-text-field
            :value="filter.name"
            clearable
            class="mt-2 pr-4"
            dense
            :label="$t('label.person_name')"
            hide-details
            style="min-width: 200px; width: 200px; max-width: 200px;"
            @input="applyFilter({...filter, name: $event ?? ''})"
          />
          <v-text-field
            :value="filter.comments"
            clearable
            class="mt-2 pr-4"
            dense
            :label="$t('label.comments')"
            hide-details
            style="min-width: 120px;"
            @input="applyFilter({...filter, comments: $event ?? ''})"
          />
          <feedback-rating-select
            :value="filter.foodRating"
            :width="140"
            :label="$t('label.rating_food')"
            @input="applyFilter({...filter, foodRating: $event})"
          />
          <feedback-rating-select
            :value="filter.serviceRating"
            :width="140"
            :label="$t('label.rating_service')"
            @input="applyFilter({...filter, serviceRating: $event})"
          />
          <feedback-rating-select
            :value="filter.ambienceRating"
            :width="140"
            :label="$t('label.rating_ambience')"
            @input="applyFilter({...filter, ambienceRating: $event})"
          />
          <feedback-rating-select
            :value="filter.overallRating"
            :width="168"
            :label="$t('label.rating_general')"
            @input="applyFilter({...filter, overallRating: $event})"
          />
          <v-spacer style="min-width: 104px; max-width: 104px;" />
        </template>
        <template v-else>
          <!-- Overflow Filter -->
          <v-spacer />
          <v-menu
            :close-on-content-click="false"
            min-width="250px"
          >
            <template #activator="{attrs, on}">
              <v-btn
                v-bind="attrs"
                icon
                v-on="on"
              >
                <v-icon>{{ filterInactive ? 'mdi-filter-outline' : 'mdi-filter' }}</v-icon>
              </v-btn>
            </template>
            <v-list class="pt-0">
              <v-list-item>
                <v-text-field
                  :value="filter.name"
                  class="px-2 py-4"
                  hide-details
                  :label="$t('label.person_name')"
                  :append-icon="filter.name.length > 0 ? 'mdi-close' : null"
                  @click:append="filter.name = ''"
                  @input="applyFilter({...filter, name: $event ?? ''})"
                />
              </v-list-item>
              <v-divider />
              <v-list-item>
                <v-text-field
                  :value="filter.comments"
                  class="px-2 py-4"
                  hide-details
                  :label="$t('label.comments')"
                  :append-icon="filter.comments.length > 0 ? 'mdi-close' : null"
                  @click:append="filter.comments = ''"
                  @input="applyFilter({...filter, comments: $event ?? ''})"
                />
              </v-list-item>
              <v-divider />
              <v-list-item>
                <feedback-rating-select
                  :value="filter.foodRating"
                  class="px-2 py-4"
                  :width="250"
                  :label="$t('label.rating_food')"
                  @input="applyFilter({...filter, foodRating: $event})"
                />
              </v-list-item>
              <v-divider />
              <v-list-item>
                <feedback-rating-select
                  :value="filter.serviceRating"
                  class="px-2 py-4"
                  :width="250"
                  :label="$t('label.rating_service')"
                  @input="applyFilter({...filter, serviceRating: $event})"
                />
              </v-list-item>
              <v-divider />
              <v-list-item>
                <feedback-rating-select
                  :value="filter.ambienceRating"
                  class="px-2 py-4"
                  :width="250"
                  :label="$t('label.rating_ambience')"
                  @input="applyFilter({...filter, ambienceRating: $event})"
                />
              </v-list-item>
              <v-divider />
              <v-list-item>
                <feedback-rating-select
                  :value="filter.overallRating"
                  class="px-2 py-4"
                  :width="250"
                  :label="$t('label.rating_general')"
                  @input="applyFilter({...filter, overallRating: $event})"
                />
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </main-toolbar>
    </template>
    <v-data-table
      class="clickable-table"
      dense
      must-sort
      :items-per-page="50"
      :headers="headers"
      :items="feedbacks"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200, -1] }"
      :custom-sort="noSort"
      :server-items-length="feedbacksLength"
      :sort-by="feedbackSortBy"
      :sort-desc="feedbackSortDesc"
      @click:row="showFeedbackDetail"
      @pagination="pagination"
      @update:sort-by="updateSortBy"
      @update:sort-desc="updateSortDesc"
      @update:options="refresh"
    >
      <template #no-data>
        <p class="py-16 my-16">
          {{ $t('message.no_feedback_received_yet') }}
        </p>
      </template>
      <template #item.created="{item}">
        {{ localized.numericDateTimeText(item.created) }}
      </template>
      <template #item.name="{item}">
        {{ item.contactName }}
        <sup v-if="item.contactReservationCount > 1">{{ item.contactReservationCount }}</sup>
      </template>
      <template #item.overallRating="{item}">
        <v-rating
          color="yellow"
          background-color="grey"
          readonly
          dense
          :value="item.overallRating"
        />
      </template>
      <template #item.foodRating="{item}">
        <feedback-rating-chip :value="item.foodRating" />
      </template>
      <template #item.serviceRating="{item}">
        <feedback-rating-chip :value="item.serviceRating" />
      </template>
      <template #item.ambienceRating="{item}">
        <feedback-rating-chip :value="item.ambienceRating" />
      </template>
      <template #item.replied="{item}">
        <v-tooltip
          v-if="!item.replied"
          :key="`replied_${item.id}`"
          open-delay="270"
          bottom
        >
          <template #activator="{on, attr}">
            <v-btn
              v-bind="attr"
              small
              icon
              v-on="on"
              @click.stop="replyClicked(item)"
            >
              <v-icon>
                mdi-reply
              </v-icon>
            </v-btn>
          </template>
          {{ $t('label.send_reply') }}
        </v-tooltip>
        <v-tooltip
          v-else
          :key="`notreplied_${item.id}`"
          open-delay="270"
          bottom
        >
          <template #activator="{on, attr}">
            <v-icon
              v-bind="attr"
              color="green"
              v-on="on"
            >
              mdi-message-check
            </v-icon>
          </template>

          {{ formatRepliedOnDate(item.replied) }}
        </v-tooltip>
      </template>
    </v-data-table>
  </toolbar-container>
</template>

<script lang="ts" setup>

import {
  ref, computed, onBeforeUnmount, getCurrentInstance, onMounted,
} from 'vue';
import { DateRange } from '@/services/date-range';

import i18n from '@/plugins/i18n';
import store from '@/store/store';
import localized from '@/plugins/vue-localized-formatter/src/localized';

import ContactMessageDialog from '@/components/views/reservation/ContactMessageDialog.vue';
import MainToolbar from '@/components/views/main/MainToolbar.vue';
import DateRangePickerField from '@/components/pickers/DateRangePickerField.vue';
import FeedbackRatingChip from '@/components/views/feedback/FeedbackRatingChip.vue';
import FeedbackRatingSelect from '@/components/views/feedback/FeedbackRatingSelect.vue';
import FeedbackDetailsDialog from '@/components/views/feedback/FeedbackDetailsDialog.vue';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import FeedbackStore from '@/store/feedback-store';
import { performDispatchAction } from '@/services/vue-utils';
import Feedback, { FeedbackFilter } from '@/grpc-api/model/feedback';
import debounce from 'lodash.debounce';
import RepeatableTimer from '@/services/repeatable-timer';
import PaywallUpgradeDialog from '@/components/dialogs/PaywallUpgradeDialog.vue';

const isFeedbackFeatureAllowed = computed<boolean>(() => store.getters.isFeedbackFeatureAllowed);

const upgradeDialog = ref(false);

const refreshTimeout = 1 * 60 * 1000; // 1 min refresh timeout

const filter = computed(() => store.getters.feedbackFilter as FeedbackFilter);

const applyFilter = (value: FeedbackFilter) => {
  store.dispatch('feedbackApplyFilter', value);
};

const filterInactive = computed(() => filter.value.overallRating.length === 5
  && filter.value.ambienceRating.length === 5
  && filter.value.serviceRating.length === 5
  && filter.value.foodRating.length === 5
  && filter.value.name.length === 0
  && filter.value.comments.length === 0);

const detailsDialog = ref(false);
const messageDialog = ref(false);

const selectedFeedback = computed(() => store.getters.selectedFeedback as Feedback|undefined);

const showFeedbackDetail = async (f: Feedback) => {
  const feedback = await performDispatchAction('getFeedback', { id: f.id }) as Feedback | undefined;
  if (!feedback) return;
  store.dispatch('setSelectedFeedback', { feedback });
  detailsDialog.value = true;
};

const replyClicked = async (f: Feedback) => {
  const feedback = await performDispatchAction('getFeedback', { id: f.id }) as Feedback | undefined;
  if (!feedback) return;
  store.dispatch('setSelectedFeedback', { feedback });
  messageDialog.value = true;
};

const closeMessageDialog = async (res: boolean | undefined) => {
  if (res && selectedFeedback.value) {
    await performDispatchAction('getFeedback', { id: selectedFeedback.value.id }) as Feedback | undefined;
  }
  messageDialog.value = false;
  store.dispatch('setSelectedFeedback', { feedback: null });
};

const headers = [
  {
    text: i18n.t('label.date_of_feedback'), align: 'start', width: '200px', value: 'created',
  },
  {
    text: i18n.t('label.person_name'), align: 'start', width: '200px', value: 'name',
  },
  {
    text: i18n.t('label.comments'), value: 'comments', class: 'comments-col', cellClass: 'comments-col',
  },
  {
    text: i18n.t('label.rating_food'), value: 'foodRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.rating_service'), value: 'serviceRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.rating_ambience'), value: 'ambienceRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.rating_general'), value: 'overallRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.responded'), value: 'replied', width: '120px', align: 'center',
  },
];

const settings = computed(() => store.getters.feedbackSettings);

const feedbacks = computed(() => store.getters.feedbackVisibleFeedbacks as Feedback[]);

const feedbacksLength = computed(() => store.getters.feedbackVisibleCount as number);

const formatRepliedOnDate = (d: Date) => i18n.t('label.reply_sent_on_x', [localized.dateText(d)!]);

// filter date range
const filterDateRange = computed(() => store.getters.feedbackDateRange as DateRange);

const filterDateRangeSelected = async (dateRange: DateRange) => {
  performDispatchAction('loadFeedback', { dateRange }, { streamLoad: true });
};

const filterDateRangeDisabled = computed(() => store.getters.isFeedbackLoading as boolean);

const load = async () => {
  if (store.getters.isFeedbackLoaded) return;
  await performDispatchAction('loadFeedback', {}, { streamLoad: true });
  upgradeDialog.value = !isFeedbackFeatureAllowed.value;
};

// debounced refresh
const debouncedRefresh = debounce(async () => {
  try {
    await store.dispatch('refreshFeedback');
  } catch (e) {
    console.log('refresh feeedback error: ', e);
  }
}, 500, { leading: true, trailing: true, maxWait: 5000 });
const refresh = async () => debouncedRefresh();

const reset = async () => {
  store.dispatch('resetFeedback');
};

let timer = undefined as RepeatableTimer | undefined;

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'feedback', FeedbackStore, async () => { load(); });
  timer = new RepeatableTimer(async () => { await debouncedRefresh(); });
  timer.start(refreshTimeout);
});

onBeforeUnmount(() => {
  timer?.stop();
  unloadStoreModule(getCurrentInstance()?.proxy!, 'feedback', async () => { reset(); });
  // tryCloseEditReservation();
});

const pagination = (p :{
  itemsLength: number,
  itemsPerPage: number
  page: number,
  pageCount: number,
  pageStart: number,
  pageStop: number,
}) => {
  console.log('feedback pagination: ', p);
  store.dispatch('updateFeedbackPage', { pageSize: p.itemsPerPage, pageStart: p.pageStart });
};
const feedbackSortBy = computed(() => store.getters.feedbackSort.sortType as string);
const feedbackSortDesc = computed(() => store.getters.feedbackSort.sortDesc as boolean);
const updateSortBy = (field: string) => store.dispatch('updateFeedbackSort', { sortType: field });
const updateSortDesc = (desc: boolean) => store.dispatch('updateFeedbackSort', { sortDesc: desc });
const noSort = (items: Feedback[]) => items;

</script>

<style>

.comments-col {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

</style>
